/**
 * SharePoint team site URL helpers (ported from tools/codiii-site-connector).
 */

export function isPersonalOneDriveUrl(url) {
  if (!url) return false;
  const u = String(url).toLowerCase();
  return (
    u.includes('/personal/')
    || u.includes(':f:/g/personal/')
    || u.includes(':f:/r/personal/')
    || u.includes('-my.sharepoint.com')
  );
}

export function isTeamSharePointUrl(url) {
  const trimmed = String(url || '').trim();
  if (!trimmed || !trimmed.toLowerCase().includes('sharepoint.com')) return false;
  return !isPersonalOneDriveUrl(trimmed);
}

export function extractTeamSiteFromSharePointUrl(inputUrl) {
  const trimmed = String(inputUrl || '').trim();
  if (!trimmed) return null;
  if (isPersonalOneDriveUrl(trimmed)) return null;

  let uri;
  try {
    uri = new URL(trimmed);
  } catch {
    return null;
  }
  if (!['http:', 'https:'].includes(uri.protocol)) return null;
  if (!uri.hostname.toLowerCase().endsWith('.sharepoint.com')) return null;

  const hostBase = `${uri.protocol}//${uri.host}`;
  const decoded = decodeURIComponent(trimmed);

  const sitesPath = decoded.match(/https?:\/\/[^/\s]+\/sites\/([^/?#\s]+)/i);
  if (sitesPath) {
    return `${hostBase}/sites/${sitesPath[1]}`.replace(/\/$/, '');
  }

  const sharingTeam = trimmed.match(/https?:\/\/[^/]+\/:f:\/s\/([^/?#]+)/i);
  if (sharingTeam) {
    return `${hostBase}/sites/${sharingTeam[1]}`.replace(/\/$/, '');
  }

  const idMatch = trimmed.match(/[?&]id=([^&]+)/);
  if (idMatch) {
    const idPath = decodeURIComponent(idMatch[1]);
    const idSite = idPath.match(/\/sites\/([^/]+)/i);
    if (idSite) {
      return `${hostBase}/sites/${idSite[1]}`.replace(/\/$/, '');
    }
  }

  return null;
}

export function normalizeTeamSiteUrl(inputUrl) {
  const trimmed = String(inputUrl || '').trim();
  if (!trimmed) {
    throw new Error('Please paste a SharePoint link.');
  }
  if (isPersonalOneDriveUrl(trimmed)) {
    throw new Error(
      'This looks like personal OneDrive. Sign in to CODiii with Microsoft for OneDrive folders.'
    );
  }
  const site = extractTeamSiteFromSharePointUrl(trimmed);
  if (site) {
    return site.replace(/\/$/, '');
  }
  throw new Error(
    'Could not find a team site in that link. Paste a folder link from a SharePoint team site.'
  );
}

export const SHAREPOINT_SITE_NOT_CONNECTED_USER_MESSAGE =
  'This SharePoint site is not connected yet. Ask your IT administrator.';

export function parseSharePointUploadError(err) {
  const raw = err?.response?.data ?? err?.message ?? err;
  let parsed = null;
  if (typeof raw === 'string') {
    try {
      const json = JSON.parse(raw);
      if (json?.code) parsed = json;
      else if (json?.message) parsed = json;
    } catch {
      if (raw.includes('SHAREPOINT_SITE_NOT_GRANTED')) {
        parsed = { code: 'SHAREPOINT_SITE_NOT_GRANTED', message: raw };
      } else if (raw.includes('manage.codiii.com') || raw.includes('SharePoint admin must grant')) {
        parsed = { code: 'SHAREPOINT_SITE_NOT_GRANTED', message: raw };
      }
    }
  } else if (raw && typeof raw === 'object' && raw.code) {
    parsed = raw;
  }

  if (parsed?.code === 'SHAREPOINT_SITE_NOT_GRANTED') {
    return {
      ...parsed,
      message: SHAREPOINT_SITE_NOT_CONNECTED_USER_MESSAGE,
    };
  }
  return parsed;
}
